import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import {
  getTransitionsNeedingProviderAttention,
  getAllTransitionsForEveryProcess,
} from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';
import { sendSendGridNotification, fetchUserDetailsAPI } from '../util/api';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

// Global "has unread inbox messages" flag (drives the nav-bar Inbox dot). Unlike
// currentUserNotificationCount (which counts sale-transitions needing provider
// attention — always ~0 for the one-transition inquiry process), this is derived
// from the newest MESSAGE per thread vs the user's per-thread last-seen time.
export const FETCH_UNREAD_INBOX_FLAG_REQUEST = 'app/user/FETCH_UNREAD_INBOX_FLAG_REQUEST';
export const FETCH_UNREAD_INBOX_FLAG_SUCCESS = 'app/user/FETCH_UNREAD_INBOX_FLAG_SUCCESS';
export const FETCH_UNREAD_INBOX_FLAG_ERROR = 'app/user/FETCH_UNREAD_INBOX_FLAG_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const FETCH_USER_DETAILS_REQUEST = 'app/user/FETCH_USER_DETAILS_REQUEST';
export const FETCH_USER_DETAILS_SUCCESS = 'app/user/FETCH_USER_DETAILS_SUCCESS';
export const FETCH_USER_DETAILS_ERROR = 'app/user/FETCH_USER_DETAILS_ERROR';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowTimestamp: 0,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasUnreadMessages: false,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  userDetailsFetchInProgress: false,
  userDetails: null,
  userDetailsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return {
        ...state,
        currentUser: mergeCurrentUser(state.currentUser, payload),
        currentUserShowTimestamp: payload ? new Date().getTime() : 0,
      };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
      };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_UNREAD_INBOX_FLAG_REQUEST:
      return { ...state };
    case FETCH_UNREAD_INBOX_FLAG_SUCCESS:
      return { ...state, currentUserHasUnreadMessages: !!payload };
    case FETCH_UNREAD_INBOX_FLAG_ERROR:
      // Non-fatal — just leave the flag as-is (no nav dot rather than a crash).
      console.error(payload); // eslint-disable-line
      return { ...state };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case FETCH_USER_DETAILS_REQUEST:
      return { ...state, userDetailsFetchInProgress: true, userDetailsError: null };
    case FETCH_USER_DETAILS_SUCCESS:
      return { ...state, userDetailsFetchInProgress: false, userDetails: payload };
    case FETCH_USER_DETAILS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, userDetailsFetchInProgress: false, userDetailsError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

export const currentUserSelector = state => {
  return state.user.currentUser;
};

export const userDetailsSelector = state => {
  return state.user.userDetails;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchUnreadInboxFlagRequest = () => ({ type: FETCH_UNREAD_INBOX_FLAG_REQUEST });
const fetchUnreadInboxFlagSuccess = hasUnread => ({
  type: FETCH_UNREAD_INBOX_FLAG_SUCCESS,
  payload: hasUnread,
});
const fetchUnreadInboxFlagError = e => ({
  type: FETCH_UNREAD_INBOX_FLAG_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const fetchUserDetailsRequest = () => ({ type: FETCH_USER_DETAILS_REQUEST });

export const fetchUserDetailsSuccess = data => ({
  type: FETCH_USER_DETAILS_SUCCESS,
  payload: data,
});

export const fetchUserDetailsError = e => ({
  type: FETCH_USER_DETAILS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

// How many threads to check for the nav-bar unread flag. IMPORTANT: the inquiry
// process never transitions after creation, so `lastTransitionedAt` (the tx sort
// key) == creation time — a NEW message on an OLD thread does NOT bubble that
// thread toward the top. So we must scan a WIDE window, not just the newest few,
// or unread messages in older threads would never light the nav dot. 100 is
// Sharetribe's max perPage and covers essentially every user at this scale.
// (A user with >100 threads could miss an unread beyond #100 on the NAV dot only
// — the InboxPage itself still shows the correct per-thread dot via pagination.)
const UNREAD_FLAG_TX_LIMIT = 100;

// Compute the global "has unread inbox messages" flag: query the user's recent
// threads, fetch the newest message per thread, and compare its time to the
// user's per-thread last-seen timestamp (currentUser.privateData.inboxLastSeen).
// A thread is unread when its newest message is from the OTHER party and is
// newer than last-seen. Fully non-fatal + client-only (SSR-guarded) — any
// failure just means no nav dot.
export const fetchUnreadInboxFlag = () => (dispatch, getState, sdk) => {
  if (typeof window === 'undefined') return Promise.resolve(false);

  const currentUser = getState().user.currentUser;
  const currentUserId = currentUser?.id?.uuid;
  if (!currentUserId) return Promise.resolve(false);

  const inboxLastSeen = currentUser?.attributes?.profile?.privateData?.inboxLastSeen || {};

  dispatch(fetchUnreadInboxFlagRequest());

  // Pull the user's most recent threads across BOTH roles (order + sale).
  return sdk.transactions
    .query({
      lastTransitions: getAllTransitionsForEveryProcess(),
      'fields.transaction': ['lastTransitionedAt'],
      page: 1,
      perPage: UNREAD_FLAG_TX_LIMIT,
    })
    .then(response => {
      const txs = response?.data?.data || [];
      if (txs.length === 0) {
        dispatch(fetchUnreadInboxFlagSuccess(false));
        return false;
      }

      const checkOne = tx => {
        const txId = tx?.id;
        const txIdUuid = txId?.uuid;
        if (!txIdUuid) return Promise.resolve(false);
        return sdk.messages
          .query({
            transaction_id: txId,
            include: ['sender'],
            'fields.message': ['createdAt'],
            page: 1,
            perPage: 1,
          })
          .then(res => {
            const msg = res?.data?.data?.[0];
            if (!msg?.attributes?.createdAt) return false;
            const senderRel = msg?.relationships?.sender?.data;
            const rawSenderId = senderRel?.id?.uuid || senderRel?.id;
            const latestSenderId = typeof rawSenderId === 'string' ? rawSenderId : null;
            // Own message is never unread.
            if (latestSenderId && latestSenderId === currentUserId) return false;
            const latestAt = new Date(msg.attributes.createdAt).getTime();
            const lastSeen = inboxLastSeen[txIdUuid];
            if (!lastSeen) return true; // incoming + never opened
            return latestAt > new Date(lastSeen).getTime();
          })
          .catch(() => false); // per-thread failure = ignore that thread
      };

      // Bounded-concurrency scan instead of Promise.all over ALL threads.
      // A power owner can have up to UNREAD_FLAG_TX_LIMIT (100) threads; firing
      // 100 sdk.messages.query calls at once trips Sharetribe's rate limiter
      // (429), which then collaterally fails ANY other in-flight API call in the
      // same window (e.g. a profile/listing Save Changes POST). Scan a few at a
      // time and short-circuit as soon as one unread is found.
      const UNREAD_SCAN_CONCURRENCY = 4;
      let hasUnread = false;
      let idx = 0;
      const worker = () => {
        if (hasUnread) return Promise.resolve();
        const i = idx++;
        if (i >= txs.length) return Promise.resolve();
        return checkOne(txs[i]).then(flag => {
          if (flag) {
            hasUnread = true;
            return; // stop this worker; others short-circuit via the flag
          }
          return worker();
        });
      };
      const workers = Array.from(
        { length: Math.min(UNREAD_SCAN_CONCURRENCY, txs.length) },
        () => worker()
      );
      return Promise.all(workers).then(() => {
        dispatch(fetchUnreadInboxFlagSuccess(hasUnread));
        return hasUnread;
      });
    })
    .catch(e => {
      dispatch(fetchUnreadInboxFlagError(storableError(e)));
      return false;
    });
};

/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve({});
  }
  // Set in-progress, no errors
  dispatch(currentUserShowRequest());

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
          // Recompute the nav-bar unread-messages dot (message-based, since the
          // transition-based notification count is ~0 for the inquiry process).
          dispatch(fetchUnreadInboxFlag());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};

export const sendNotification = params => {
  return async (dispatch, getState, sdk) => {
    try {
      const response = await sendSendGridNotification({ ...params });
      console.log(response, 'send-notification-response');
    } catch (error) {
      console.log(error, 'send-notification-failed');
    }
  };
};

export const fetchUserDetails = body => async (dispatch, getState, sdk) => {
  dispatch(fetchUserDetailsRequest());
  try {
    const response = await fetchUserDetailsAPI(body);
    dispatch(fetchUserDetailsSuccess(response.user));
    return response;
  } catch (e) {
    dispatch(fetchUserDetailsError(storableError(e)));
  }
};
